import React, {
  useRef,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useCallback,
} from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { BryntumGrid } from '@bryntum/schedulerpro-react';
import type { Grid, GridColumnConfig, Model } from '@bryntum/schedulerpro';
import { TaskInfo } from '@dispatch/shared/types';
import {
  MANUAL_UNASSIGNMENT_LABEL,
  MANUAL_UNASSIGNMENT_TEST_ID,
  PERSON_REMOVE_OUTLINED_SVG_PATH,
} from '@dispatch/web/ui';
import { escapeHtml } from '../utils/escapeHtml';

/**
 * Static HTML twin of the MUI ManualUnassignmentChip (Bryntum template cells
 * cannot render React): same label, same PersonRemoveOutlined glyph, outlined
 * in the theme's info colour. Built only from constants and the theme colour,
 * so it interpolates no user data and needs no escaping. Styled inline because
 * the template's classes have no stylesheet rules.
 */
const manualUnassignmentPill = (color: string) =>
  `<span data-testid="${MANUAL_UNASSIGNMENT_TEST_ID}" style="display:inline-flex;align-items:center;gap:2px;max-width:100%;box-sizing:border-box;padding:0 6px;border:1px solid ${color};border-radius:12px;color:${color};font-size:10px;line-height:16px;white-space:nowrap;"><svg viewBox="0 0 24 24" aria-hidden="true" width="12" height="12" fill="currentColor" style="flex-shrink:0;"><path d="${PERSON_REMOVE_OUTLINED_SVG_PATH}"></path></svg><span style="overflow:hidden;text-overflow:ellipsis;">${MANUAL_UNASSIGNMENT_LABEL}</span></span>`;

/**
 * The grid record is a Bryntum Model wrapping the TaskInfo under `.data` (see
 * handleCellClick); plain objects carry the field directly.
 */
const isManuallyUnassigned = (record: TaskInfo) =>
  record?.wasManuallyUnassigned === true ||
  (record as { data?: TaskInfo })?.data?.wasManuallyUnassigned === true;

interface UnassignedGridProps {
  tasks: TaskInfo[];
  isLoading?: boolean;
  onTaskClick?: (task: TaskInfo) => void;
  onTaskSchedule?: (
    taskId: string,
    params: {
      resourceId: string;
      startDate: string;
      endDate: string;
    }
  ) => Promise<void>;
}

/**
 * UnassignedGrid - Grid component for displaying unassigned tasks
 *
 * Tasks can be dragged from this grid to the scheduler to assign them
 */
export const UnassignedGrid = forwardRef<
  { instance: unknown },
  UnassignedGridProps
>(({ tasks, isLoading = false, onTaskClick }, ref) => {
  const gridRef = useRef<BryntumGrid>(null);
  const theme = useTheme();
  const pillHtml = useMemo(
    () => manualUnassignmentPill(theme.palette.info.main),
    [theme.palette.info.main]
  );
  // The 350px panel splits evenly into ~110px columns, too narrow for the
  // pill; while any card carries it, give the first column the room.
  const hasManualUnassignment = tasks.some(isManuallyUnassigned);
  const handleCellClick = useCallback(
    (event: { grid: Grid; record: Model }) => {
      // Bryntum Grid records are typed as `Model`, but our grid `data` prop
      // passes `TaskInfo[]`, so each record carries the original payload in `.data`.
      const record = event.record as Model & { data?: TaskInfo };
      const task = record?.data;
      if (task && onTaskClick) {
        onTaskClick(task);
      }
    },
    [onTaskClick]
  );

  useImperativeHandle(ref, () => ({
    get instance() {
      return gridRef.current?.instance;
    },
  }));

  const columns = useMemo(
    () => [
      {
        type: 'template' as const,
        flex: hasManualUnassignment ? 1.3 : 1,
        field: 'clientInfo',
        cellCls: 'unscheduled-name-cell',
        template: ({ record }: { record: TaskInfo }) => `
            <div class="unassigned-task-info">
              <div class="task-insured">${escapeHtml(record?.insuredName)}</div>
              <div class="task-claim">${escapeHtml(record?.claimNumber)}</div>
              ${
                isManuallyUnassigned(record)
                  ? `<div class="task-manual-unassignment">${pillHtml}</div>`
                  : ''
              }
            </div>
          `,
      },
      {
        type: 'template' as const,
        field: 'taskDetails',
        flex: hasManualUnassignment ? 0.9 : 1,
        template: ({ record }: { record: TaskInfo }) => `
            <div class="unassigned-task-location">
              <div class="task-location">${escapeHtml(
                record?.lossCity
              )}, ${escapeHtml(record?.lossState)}</div>
              <div class="task-appointment">${escapeHtml(
                record?.appointment
              )}</div>
            </div>
          `,
      },
      {
        type: 'template' as const,
        flex: hasManualUnassignment ? 0.8 : 1,
        field: 'taskType',
        template: ({ record }: { record: TaskInfo }) => `
            <div class="unassigned-task-type">
              <div class="task-method">${escapeHtml(record?.peril)}</div>
              <div class="task-duration">${
                record?.appointmentDurationInMinutes || 0
              } min</div>
            </div>
          `,
      },
    ],
    [pillHtml, hasManualUnassignment]
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: 350,
        borderLeft: '1px solid',
        borderColor: 'divider',
        borderTopRightRadius: '5px',
        borderBottomRightRadius: '5px',
      }}
    >
      <Box
        sx={{
          p: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Typography variant="subtitle2" fontWeight="bold">
          Unassigned Assignments
        </Typography>
      </Box>
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <BryntumGrid
          ref={gridRef}
          id="scheduler-grid"
          hideHeaders={true}
          selectionMode={{
            row: true,
            cell: false,
            multiSelect: false,
          }}
          stripeFeature={true}
          // Our column config includes custom renderer properties that Bryntum's
          // `GridColumnConfig` type doesn't declare — double cast bridges the gap.
          columns={columns as unknown as GridColumnConfig[]}
          rowHeight={65}
          disableGridRowModelWarning={true}
          data={tasks}
          loadMask={isLoading ? 'Loading...' : null}
          onCellClick={handleCellClick}
        />
      </Box>
    </Box>
  );
});

UnassignedGrid.displayName = 'UnassignedGrid';
